import React, { useCallback, useRef, useState } from 'react';
import { FiArrowLeft, FiMail } from 'react-icons/fi';
import { Link as ReactLink, useHistory } from 'react-router-dom';

import {
  Button,
  Flex,
  Heading,
  Box,
  useColorMode,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import Input from '~/shared/components/InputChakra';
import api from '~/shared/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgColor = { light: 'gray.50', dark: 'rgb(18, 18, 20)' };
  const bgInputColor = { light: '#fff', dark: 'rgb(23, 23, 26)' };
  const bgBorderInputColor = { light: 'inherit', dark: 'rgb(40, 39, 44)' };
  const textInputColor = { light: 'black', dark: 'rgb(225, 225, 230)' };
  const textTitle = { light: 'gray.700', dark: 'rgb(225, 225, 230)' };

  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const formRef = useRef<FormHandles>(null);

  const addToast = useToast();

  const handleSubmit = useCallback(
    async (data: ForgotPasswordFormData) => {
      try {
        setLoading(true);

        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string()
            .required('E-mail obrigatório')
            .email('Digite um e-mail válido!'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api.post('/password/forgot', {
          email: data.email,
        });

        addToast({
          position: 'top-right',
          isClosable: true,
          status: 'success',
          title: 'E-mail de recuperação enviado',
          description:
            'Enviamos um e-mail para confirmar a recuperação de senha, cheque sua caixa de entrada',
        });
        history.push('/');
      } catch (err) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          position: 'top-right',
          isClosable: true,
          status: 'error',
          title: 'Erro na recuperação de senha',
          description:
            'Ocorreu um erro ao tentar realizar a recuperação de senha. tente novamente',
        });
      }
    },
    [addToast, history],
  );

  return (
    <>
      <Box
        w="full"
        display="flex"
        flexDir="column"
        alignItems="center"
        minH="100vh"
        bg={bgColor[colorMode]}
        transition="all 0.2s"
      >
        <Box
          display="flex"
          flex="1 1 0%"
          flexDir="column"
          alignItems="center"
          w="100%"
          padding="32px"
          maxW={1000}
        >
          <Flex
            flex="1 1 0%"
            justifyContent="center"
            w="full"
            alignItems="center"
            padding="28px 0 50px"
          >
            <Box w="full" maxW="320px">
              <Form ref={formRef} onSubmit={handleSubmit}>
                <Heading
                  as="h1"
                  fontSize="25px"
                  mb="29px"
                  color={textTitle[colorMode]}
                  textAlign="center"
                >
                  Recuperar senha
                </Heading>
                <Input
                  name="email"
                  icon={FiMail}
                  placeholder="Digite seu e-mail"
                  autoFocus
                  background={bgInputColor[colorMode]}
                  borderColor={bgBorderInputColor[colorMode]}
                  color={textInputColor[colorMode]}
                />

                <Button
                  mt={4}
                  colorScheme="blue"
                  isLoading={loading}
                  isDisabled={loading}
                  width="100%"
                  size="lg"
                  type="submit"
                >
                  RECUPERAR
                </Button>

                <ReactLink to="/">
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    w="full"
                    textAlign="center"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt={5}
                  >
                    <FiArrowLeft />
                    Voltar
                  </Text>
                </ReactLink>
              </Form>
            </Box>
          </Flex>
        </Box>
      </Box>
    </>
  );
};

export default ForgotPassword;
